import { useParams, Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { PageHero, Section, Btn, ArrowLink, SectionHead } from "@/components/kit";
import { Reveal, Eyebrow } from "@/lib/anim";
import { COUNTRIES, COUNTRY_LINKS } from "@/lib/content";
import { COUNTRY_IMG } from "@/lib/images";
import NotFound from "@/pages/NotFound";

export default function Country() {
  const { slug } = useParams();
  const d = COUNTRIES[slug];
  if (!d) return <NotFound />;
  const others = COUNTRY_LINKS.filter((c) => c.slug !== slug);

  return (
    <>
      <Seo title={`${d.name} Sourcing & Clothing Manufacturers for Australian Brands`} canonical={`/sourcing/${slug}`} description={d.body} keywords={`${d.name} sourcing, clothing manufacturers ${d.name}, apparel manufacturers ${d.name}, fashion suppliers ${d.name}, factory sourcing ${d.name}, product sourcing Australia`} crumbs={[{ label: "Home", to: "/" }, { label: "Sourcing", to: "/sourcing" }, { label: d.name }]} />
      <PageHero
        eyebrow="Sourcing"
        crumbs={[{ label: "Home", to: "/" }, { label: "Sourcing", to: "/sourcing" }, { label: d.name }]}
        title={d.h}
        lead={d.body}
      />
      <Section bg="bone" pad={false} className="py-12">
        <div className="aspect-[21/9] overflow-hidden" style={{ borderRadius: 2 }}>
          <img src={COUNTRY_IMG[slug]} alt="" className="img-cover duo" loading="lazy" />
        </div>
      </Section>

      <Section bg="paper">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <Eyebrow>Good for</Eyebrow>
            <div className="prose max-w-none"><ul>{d.good.map((g) => <li key={g}>{g}</li>)}</ul></div>
          </div>
          <div>
            <Eyebrow>Less suited to</Eyebrow>
            <div className="prose max-w-none"><ul>{d.bad.map((g) => <li key={g}>{g}</li>)}</ul></div>
          </div>
        </div>
      </Section>

      <Section bg="charcoal" dark pad={false} className="py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {Object.entries(d.terms).map(([k, v]) => (
            <div key={k}>
              <p className="font-mono text-[12px] uppercase tracking-wide text-[var(--muted-dark)]">{k === "Watch" ? "Watch for" : k === "Lead" ? "Lead time" : k}</p>
              <p className="t-body text-[var(--bone)] mt-2">{v}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section bg="sand">
        <SectionHead eyebrow="Mistakes to avoid" title={`Common errors sourcing from ${d.name}.`} />
        <ul className="grid md:grid-cols-3 gap-6 mt-10">
          {d.mistakes.map((m, i) => (
            <li key={m} className="card p-6"><span className="font-mono text-[var(--clay)]">0{i + 1}</span><p className="t-body mt-3">{m}</p></li>
          ))}
        </ul>
      </Section>

      <Section bg="bone">
        <Eyebrow>Other countries</Eyebrow>
        <div className="flex flex-wrap gap-3 mt-4">
          {others.map((o) => (
            <Link key={o.slug} to={`/sourcing/${o.slug}`} className="btn btn-outline">{o.name}</Link>
          ))}
        </div>
      </Section>

      <Section bg="charcoalDeep" dark className="py-20 md:py-24">
        <div className="max-w-3xl">
          <h2 className="t-display2">Build a shortlist in {d.name}.</h2>
          <p className="t-lead mt-6 text-[var(--stone)]">A Supplier Search qualifies factories and negotiates quotes against your specification.</p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Btn to="/services/supplier-and-factory-sourcing" variant="clay">See Supplier Search</Btn>
            <Btn to="/book" variant="outline-bone">Book a fit call</Btn>
          </div>
        </div>
      </Section>
    </>
  );
}
